import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import Modal from "../components/Modal";
import ConfirmModal from "../components/ConfirmModal";
import StatusBadge from "../components/StatusBadge";
import { capitalizeFirstLetter, capitalizeWords } from "../../utils/letters";
import { formatDate } from "../../utils/date";
import {
	INVALID_RUT_MESSAGE,
	RUT_DIGITS_HINT,
	commitRutField,
	formatRut,
	sanitizeRutDigitsOnly,
	toApiRut,
	validateRut,
} from "../../utils/rutUtils";
import {
	createCompanyAdmin,
	createCompanyContractMaster,
	createCompanyEntitlement,
	disableCompanyAdmin,
	enableCompanyAdmin,
	generateCompanyCode,
	getCompany,
	listCompanyAdmins,
	listCompanyContractsMaster,
	listCompanyEntitlements,
	lookupCourseRuns,
	resetCompanyAdminPassword,
	updateCompanyContractMaster,
	updateCompanyEntitlement,
} from "../api/companies";

/** Solo letras y espacios (incluye acentos y ñ). Para nombres y apellidos. */
function validateLettersAndSpaces(value) {
	if (!value || !String(value).trim()) return true;
	return /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]+$/.test(String(value).trim());
}

function normalizeRutValidationMessage(message) {
	const text = String(message ?? "").trim();
	if (!text) return text;
	if (/rut/i.test(text) && /(inv[aá]lido|formato|v[aá]lido)/i.test(text)) {
		return INVALID_RUT_MESSAGE;
	}
	return text;
}
function getTodayIsoDate() {
	const now = new Date();
	const offsetMilliseconds = now.getTimezoneOffset() * 60 * 1000;
	return new Date(now.getTime() - offsetMilliseconds)
		.toISOString()
		.slice(0, 10);
}

const ACCESS_GRACE_DAYS = 30;

function addDaysIso(dateValue, days) {
	if (!dateValue) return false;
	const date = new Date(`${String(dateValue).slice(0, 10)}T00:00:00`);
	date.setDate(date.getDate() + days);
	return date.toISOString().slice(0, 10);
}

function isExpiredByDate(dateValue, graceDays = 0) {
	if (!dateValue) return false;
	return addDaysIso(dateValue, graceDays) < getTodayIsoDate();
}

export default function CompanyDetail() {
	const navigate = useNavigate();
	const { id } = useParams();
	const companyId = Number(id);

	const [admins, setAdmins] = useState([]);
	const [alert, setAlert] = useState(null); // {type,msg}
	const [company, setCompany] = useState(null);
	const [courseRunLoading, setCourseRunLoading] = useState(false);
	const [courseRunOptions, setCourseRunOptions] = useState([]);
	const [courseRunQuery, setCourseRunQuery] = useState("");
	const [entitlements, setEntitlements] = useState([]);
	const [entModalOpen, setEntModalOpen] = useState(false);
	const [entSaving, setEntSaving] = useState(false);
	const [error, setError] = useState("");
	const [masterContracts, setMasterContracts] = useState([]);
	const [mcModalOpen, setMcModalOpen] = useState(false);
	const [mcSaving, setMcSaving] = useState(false);
	const [mcEditing, setMcEditing] = useState(null); // contrato a editar o null para crear
	const [mcTogglingId, setMcTogglingId] = useState(null);
	const [generatedCode, setGeneratedCode] = useState("");
	const [selectedCourseRun, setSelectedCourseRun] = useState(null);

	const [entForm, setEntForm] = useState({
		course_run_id: "",
		max_students: "",
		is_active: true,
	});
	const [mcForm, setMcForm] = useState({
		start_date: "",
		end_date: "",
		is_active: true,
	});
	const [lastAccessCode, setLastAccessCode] = useState(null);

	// Modal: Crear representante (acceso por contrato de la organización)
	const [claveModalOpen, setClaveModalOpen] = useState(false);
	const [claveForm, setClaveForm] = useState({
		email: "",
		first_name: "",
		last_name: "",
		rut: "",
	});
	const [claveRutError, setClaveRutError] = useState("");
	const [claveSaving, setClaveSaving] = useState(false);
	const [claveResult, setClaveResult] = useState(null); // { username, tempPassword } para entregar al representante
	const [entFieldErrors, setEntFieldErrors] = useState({});
	const [mcFieldErrors, setMcFieldErrors] = useState({});
	const [claveFieldErrors, setClaveFieldErrors] = useState({});

	const [searchParams, setSearchParams] = useSearchParams();
	const [disablingAdminId, setDisablingAdminId] = useState(null);
	const [resettingPasswordId, setResettingPasswordId] = useState(null);
	const [resetPasswordTarget, setResetPasswordTarget] = useState(null);
	const [togglingEntitlementId, setTogglingEntitlementId] = useState(null);

	async function load() {
		setError("");
		try {
			const c = await getCompany(companyId);
			setCompany(c);
			// Siempre sincronizar código Excel con la organización cargada (evita mostrar código de otra organización)
			setGeneratedCode(c?.excel_code ?? "");
		} catch (e) {
			setError(
				"No se pudo cargar esta organización. Comprueba que exista o que tengas permiso.",
			);
			return;
		}
		// Cargar cada lista por separado para que un fallo no borre las demás (p. ej. periodos recién creados)
		try {
			const asList = await listCompanyAdmins(companyId);
			setAdmins(Array.isArray(asList) ? asList : (asList?.results ?? []));
		} catch {
			setAdmins([]);
		}
		try {
			const contractsList = await listCompanyContractsMaster(companyId);
			const list = Array.isArray(contractsList)
				? contractsList
				: (contractsList?.results ?? []);
			setMasterContracts(list);
		} catch {
			setMasterContracts((prev) => (prev.length ? prev : []));
		}
		try {
			const entsList = await listCompanyEntitlements(companyId);
			setEntitlements(
				Array.isArray(entsList) ? entsList : (entsList?.results ?? []),
			);
		} catch {
			setEntitlements([]);
		}
	}

	async function handleGenerateCode() {
		try {
			const res = await generateCompanyCode(companyId);
			// El backend devuelve el código existente o el recién generado; nunca sobrescribe en DB
			setGeneratedCode(res.code ?? "");
			if (company && !company.excel_code && res.code) {
				setCompany((prev) =>
					prev ? { ...prev, excel_code: res.code } : prev,
				);
			}
		} catch {
			setError("No se pudo generar el código. Intenta nuevamente.");
		}
	}

	useEffect(() => {
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyId]);

	// Abrir modal de crear representante si viene ?crear_rep=1 desde la lista de organización
	useEffect(() => {
		if (company && searchParams.get("crear_rep") === "1") {
			setClaveModalOpen(true);
			setClaveResult(null);
			setClaveForm({
				email: "",
				first_name: "",
				last_name: "",
				rut: "",
			});
			setClaveRutError("");
			setClaveFieldErrors({});
			setSearchParams({}, { replace: true });
		}
	}, [company, searchParams, setSearchParams]);

	// Búsqueda de runs al escribir (modal Agregar curso). El run = curso + fechas = periodo de vigencia (2x1).
	useEffect(() => {
		if (!entModalOpen) return;
		let alive = true;
		const t = setTimeout(async () => {
			setCourseRunLoading(true);
			try {
				const params =
					courseRunQuery.trim().length >= 1
						? { search: courseRunQuery.trim() }
						: {};
				const data = await lookupCourseRuns(params);
				const results = Array.isArray(data)
					? data
					: (data.results ?? []);
				if (alive) setCourseRunOptions(results);
			} catch {
				if (alive) setCourseRunOptions([]);
			} finally {
				if (alive) setCourseRunLoading(false);
			}
		}, 250);
		return () => {
			alive = false;
			clearTimeout(t);
		};
	}, [courseRunQuery, entModalOpen]);

	function openClaveModal() {
		if (company && !company.is_active) {
			setError("No se pueden crear representantes en una organización desactivada.");
			return;
		}
		setClaveResult(null);
		setClaveForm({
			email: "",
			first_name: "",
			last_name: "",
			rut: "",
		});
		setClaveRutError("");
		setClaveModalOpen(true);
	}

	function askResetCompanyAdminPassword(admin) {
		setResetPasswordTarget(admin);
	}

	async function confirmResetCompanyAdminPassword() {
		if (!resetPasswordTarget) return;

		setResettingPasswordId(resetPasswordTarget.id);
		setError("");
		try {
			const res = await resetCompanyAdminPassword(
				companyId,
				resetPasswordTarget.id,
			);
			setLastAccessCode({
				username: res.user?.username ?? resetPasswordTarget.username,
				tempPassword: res.temp_password,
			});
			setResetPasswordTarget(null);
			await load();
		} catch (e) {
			setError(
				e?.response?.data?.error ||
				"No se pudo restablecer la contraseña.",
			);
		} finally {
			setResettingPasswordId(null);
		}
	}

	async function submitClaveTemporal(e) {
		e.preventDefault();
		const err = {};
		if (!(claveForm.first_name ?? "").trim())
			err.first_name = "Los nombres son obligatorios.";
		if (!(claveForm.last_name ?? "").trim())
			err.last_name = "Los apellidos son obligatorios.";
		if (!(claveForm.email ?? "").trim())
			err.email = "El email es obligatorio.";
		if (!(claveForm.rut ?? "").trim()) err.rut = "El RUT es obligatorio.";
		else if (!validateRut(claveForm.rut).valid) {
			setClaveRutError(INVALID_RUT_MESSAGE);
			setClaveFieldErrors((p) => ({ ...p, rut: INVALID_RUT_MESSAGE }));
			return;
		}
		if (
			!validateLettersAndSpaces(claveForm.first_name) &&
			(claveForm.first_name ?? "").trim()
		)
			err.first_name = "Nombres debe contener solo letras y espacios.";
		if (
			!validateLettersAndSpaces(claveForm.last_name) &&
			(claveForm.last_name ?? "").trim()
		)
			err.last_name = "Apellidos debe contener solo letras y espacios.";
		setClaveFieldErrors(err);
		if (Object.keys(err).length > 0) return;

		const normalizedRut = toApiRut(claveForm.rut);
		setClaveSaving(true);
		setClaveRutError("");
		setClaveResult(null);
		setClaveFieldErrors({});
		try {
			const payload = {
				email: (claveForm.email || "").trim(),
				first_name: (claveForm.first_name || "").trim(),
				last_name: (claveForm.last_name || "").trim(),
				rut: normalizedRut,
			};
			const res = await createCompanyAdmin(companyId, payload);
			setClaveResult({
				username: res.user?.username || "",
				tempPassword: res.temp_password,
			});
			setLastAccessCode({
				username: res.user?.username || "",
				tempPassword: res.temp_password,
			});
			await load();
		} catch (err) {
			const msgRaw =
				err.response?.data?.detail ??
				err.response?.data?.rut?.[0] ??
				err.response?.data?.username?.[0] ??
				err.response?.data?.email?.[0];
			const msg = normalizeRutValidationMessage(msgRaw);
			if (msg === INVALID_RUT_MESSAGE) {
				setClaveRutError(INVALID_RUT_MESSAGE);
				setClaveFieldErrors((p) => ({
					...p,
					rut: INVALID_RUT_MESSAGE,
				}));
				return;
			}
			setClaveResult({
				error:
					msg ||
					"No se pudo crear. Revisa RUT y correo (deben ser únicos).",
			});
		} finally {
			setClaveSaving(false);
		}
	}

	function openEntitlementModal() {
		setAlert(null);
		setEntFieldErrors({});
		setEntForm({ course_run_id: "", max_students: "", is_active: true });
		setCourseRunQuery("");
		setCourseRunOptions([]);
		setEntModalOpen(true);
	}

	// Al abrir el modal cargamos runs; al escribir buscamos con debounce.
	useEffect(() => {
		if (!entModalOpen) return;
		let cancelled = false;
		const query = (courseRunQuery || "").trim();
		const params = query.length >= 1 ? { search: query } : {};
		const delay = query.length >= 1 ? 280 : 0; // debounce solo al escribir
		const t = setTimeout(async () => {
			setCourseRunLoading(true);
			try {
				const data = await lookupCourseRuns(params);
				const results = Array.isArray(data)
					? data
					: (data.results ?? []);
				if (!cancelled) setCourseRunOptions(results);
			} catch {
				if (!cancelled) setCourseRunOptions([]);
			} finally {
				if (!cancelled) setCourseRunLoading(false);
			}
		}, delay);
		return () => {
			cancelled = true;
			clearTimeout(t);
		};
	}, [entModalOpen, courseRunQuery]);

	async function submitEntitlement(e) {
		e?.preventDefault();
		setAlert(null);
		const err = {};
		if (!entForm.course_run_id)
			err.course_run_id = "Elige un curso (con sus fechas).";
		const requestedSlots = Number(entForm.max_students);
		const courseMaxSlots =
			selectedCourseRun?.max_students != null
				? Number(selectedCourseRun.max_students)
				: null;
		if (!entForm.max_students) {
			err.max_students = "Ingresa la cantidad de cupos.";
		} else if (!Number.isFinite(requestedSlots) || requestedSlots <= 0) {
			err.max_students = "Los cupos deben ser mayor a 0.";
		} else if (
			Number.isFinite(courseMaxSlots) &&
			requestedSlots > courseMaxSlots
		) {
			err.max_students = `No puede superar el máximo del curso (${courseMaxSlots}).`;
		}
		setEntFieldErrors(err);
		if (Object.keys(err).length > 0) return;

		setEntSaving(true);
		try {
			await createCompanyEntitlement(companyId, {
				course_run_id: Number(entForm.course_run_id),
				max_students: Number(entForm.max_students),
				is_active: Boolean(entForm.is_active),
			});

			setAlert({
				type: "success",
				msg: "Curso asignado. Las fechas del curso son el periodo de vigencia de la organización.",
			});
			setEntModalOpen(false);
			setEntFieldErrors({});
			await load();
		} catch (err) {
			const d = err?.response?.data;
			const msg =
				d?.detail ||
				d?.course_run_id?.[0] ||
				(typeof d === "object"
					? Object.values(d).flat().find(Boolean)
					: null);
			setAlert({
				type: "danger",
				msg:
					msg ||
					"No se pudo asignar. Revisa que ese curso no esté ya asignado a la organización.",
			});
		} finally {
			setEntSaving(false);
		}
	}

	function openEditMasterContractModal(c) {
		setAlert(null);
		setMcFieldErrors({});
		setMcEditing(c);
		setMcForm({
			start_date: c.start_date || "",
			end_date: c.end_date || "",
			is_active: Boolean(c.is_active),
		});
		setMcModalOpen(true);
	}

	async function submitMasterContract(e) {
		e?.preventDefault();
		setAlert(null);
		const err = {};
		if (!(mcForm.start_date ?? "").trim())
			err.start_date = "La fecha de inicio es obligatoria.";
		if (!(mcForm.end_date ?? "").trim())
			err.end_date = "La fecha de fin es obligatoria.";
		setMcFieldErrors(err);
		if (Object.keys(err).length > 0) return;

		const payload = {
			start_date: mcForm.start_date,
			end_date: mcForm.end_date,
			is_active: Boolean(mcForm.is_active),
		};

		setMcSaving(true);
		try {
			if (mcEditing) {
				await updateCompanyContractMaster(
					companyId,
					mcEditing.id,
					payload,
				);
				setAlert({ type: "success", msg: "Periodo actualizado." });
			} else {
				const created = await createCompanyContractMaster(
					companyId,
					payload,
				);
				setAlert({
					type: "success",
					msg: "Periodo creado. Ya puedes agregar cursos para esta organización.",
				});
				if (created && (created.id || created.start_date)) {
					setMasterContracts((prev) => [created, ...prev]);
				}
			}
			setMcModalOpen(false);
			setMcEditing(null);
			await load();
		} catch (err) {
			const data = err.response?.data;
			const msg =
				(data && (data.end_date?.[0] || data.detail)) ||
				(data &&
					typeof data === "object" &&
					Object.values(data).flat().find(Boolean)) ||
				(mcEditing
					? "No se pudo actualizar el periodo."
					: "No se pudo crear. Revisa que la fecha de fin sea después de la de inicio.");
			setAlert({
				type: "danger",
				msg:
					typeof msg === "string"
						? msg
						: "No se pudo guardar el periodo.",
			});
		} finally {
			setMcSaving(false);
		}
	}

	async function toggleMasterContractActive(c) {
		setMcTogglingId(c.id);
		setError("");
		try {
			await updateCompanyContractMaster(companyId, c.id, {
				is_active: !c.is_active,
			});
			await load();
		} catch (e) {
			setError(
				e?.response?.data?.detail ||
				e?.response?.data?.error ||
				"No se pudo actualizar el periodo.",
			);
		} finally {
			setMcTogglingId(null);
		}
	}

	if (!company) {
		return (
			<div className="container-fluid">
				{error ? (
					<div className="alert alert-danger" role="alert">
						{error}
					</div>
				) : (
					<p className="text-muted">Cargando...</p>
				)}
			</div>
		);
	}

	return (
		<div className="container-fluid">
			<div className="d-flex justify-content-between align-items-start gap-3 mb-3 flex-wrap">
				<div>
					<div className="btn-back-container">
						<button onClick={() => navigate(-1)}>
							<i className="ri-arrow-go-back-line d-inline d-md-none"></i>
							<span className="d-none d-md-inline">Volver</span>
						</button>
					</div>
					<h2 style={{ marginTop: 0 }}>{company.name}</h2>
					<div className="admin-subtittle" style={{ fontSize: 14 }}>
						<span>
							RUT: {formatRut(company.rut) || company.rut}
						</span>
						<span>
							{" "}
							· Activa:{" "}
							<StatusBadge
								active={Boolean(company.is_active)}
								title="Organización activa"
							/>
						</span>
						{company.created_at && (
							<span>
								{" "}
								· Alta: {formatDate(company.created_at)}
							</span>
						)}
					</div>
				</div>
				<div className="d-flex gap-2 flex-wrap">
					<div className="card card-body-excel">
						<div className="card-body py-2 px-3 ">
							<h5 className="mb-0" style={{ fontSize: 18 }}>
								Código para Excel
							</h5>
							<div className="d-flex gap-2 align-items-center mt-1">
								<button
									type="button"
									className="btn-sm btn-excel"
									onClick={handleGenerateCode}
								>
									<i
										className="ri-refresh-line me-md-1"
										aria-hidden="true"
									></i>
									<span className="d-none d-md-inline">
										Obtener código
									</span>
								</button>
								<input
									className="form-control form-control-sm form-control-excel"
									style={{ width: 180 }}
									readOnly
									value={generatedCode}
									placeholder="Aparecerá aquí"
									onFocus={(e) => e.target.select()}
								/>
							</div>
							<small
								className="text-muted"
								style={{ fontSize: 11 }}
							>
								<span>
									Código único por organización; no se puede
									crear otro.
								</span>
							</small>
						</div>
					</div>
					<div className="card card-body-repres">
						<div className="card-body py-2 px-3">
							<h5 className="mb-0" style={{ fontSize: 18 }}>
								Crear representante
							</h5>
							<p
								className="mb-2 mt-1 small text-muted"
								style={{ fontSize: 12 }}
							>
								<span>
									Acceso para subir Excel e inscribir alumnos.
								</span>
							</p>
							<button
								type="button"
								className="btn-sm btn-repres"
								onClick={openClaveModal}
								disabled={company && !company.is_active}
								title={
									company && !company.is_active
										? "No se pueden crear representantes en una organización desactivada."
										: "Crear representante"
								}
							>
								<i
									className="ri-user-add-fill me-md-1"
									aria-hidden="true"
								/>
								<span className="d-none d-md-inline">
									Crear representante
								</span>
							</button>
						</div>
					</div>
				</div>
			</div>

			{error && (
				<div className="alert alert-danger mt-3" role="alert">
					<i
						className="ri-error-warning-line me-2"
						aria-hidden="true"
					></i>
					<span>{error}</span>
				</div>
			)}

			<section className="card mb-3 card-table">
				<div className="card-body">
					<div className="d-flex justify-content-between align-items-end gap-2">
						<div>
							<h5 className="mb-2 fw-bold">Cursos asignados</h5>
							<div
								className="text-muted"
								style={{ fontSize: 12 }}
							>
								<span>
									Asigna un curso; sus fechas de inicio y fin
									serán el periodo de vigencia de la
									organización.
								</span>
							</div>
						</div>
						<button
							className="admin-btn text-nowrap"
							onClick={openEntitlementModal}
						>
							<i
								className="ri-add-line me-md-1"
								aria-hidden="true"
							></i>
							<span className="d-none d-md-inline">
								Agregar curso
							</span>
						</button>
					</div>

					<div className="table-responsive mt-3 table-wrapper-scroll">
						<table className="table table-hover align-middle mb-0">
							<thead>
								<tr>
									<th>
										<span>Curso</span>
									</th>
									<th>
										<span>Inicio</span>
									</th>
									<th>
										<span>Fin</span>
									</th>
									<th>
										<span>Cupos</span>
									</th>
									<th>
										<span>Activo</span>
									</th>
									<th>
										<span>Acciones</span>
									</th>
								</tr>
							</thead>
							<tbody>
								{entitlements.length === 0 ? (
									<tr>
										<td
											colSpan="6"
											className="text-muted py-4"
										>
											<span>
												Aún no hay cursos. Usa “Agregar
												curso” y elige un curso (sus
												fechas serán el periodo de
												vigencia).
											</span>
										</td>
									</tr>
								) : (
									entitlements.map((x) => {
										const linkedPeriod =
											masterContracts.find(
												(c) =>
													Number(c.id) ===
													Number(x.contract),
											);
										const linkedPeriodInactive =
											linkedPeriod &&
											!linkedPeriod.is_active;
										const entitlementExpired =
											isExpiredByDate(
												x.contract_end_date,
												ACCESS_GRACE_DAYS,
											);
										const cannotEnableInactivePeriod =
											!x.is_active &&
											linkedPeriodInactive;
										return (
											<tr key={x.id}>
												<td>
													<strong>
														<span>
															{x.course_code}
														</span>
													</strong>
													<span>
														{" "}
														—{" "}
														{capitalizeFirstLetter(
															x.course_name,
														)}
													</span>
												</td>
												<td>
													<span>
														{formatDate(
															x.contract_start_date,
														)}
													</span>
												</td>
												<td>
													<span>
														{formatDate(
															x.contract_end_date,
														)}
													</span>
												</td>
												<td>
													<span>
														{x.max_students}
													</span>
												</td>
												<td>
													<span>
														{x.is_active
															? "Sí"
															: "No"}
													</span>
												</td>
												<td>
													<button
														type="button"
														className={`btn-sm text-nowrap ${x.is_active ? "adm-del-btn" : "adm-save-btn"}`}
														disabled={
															togglingEntitlementId ===
															x.id ||
															entitlementExpired ||
															cannotEnableInactivePeriod
														}
														title={
															entitlementExpired
																? "El tiempo de gracia del curso ya venció y no puede volver a habilitarse."
																: cannotEnableInactivePeriod
																	? "Primero debes habilitar el periodo de vigencia."
																: undefined
														}
														onClick={async () => {
															setTogglingEntitlementId(
																x.id,
															);
															setError("");
															try {
																await updateCompanyEntitlement(
																	companyId,
																	x.id,
																	{
																		is_active:
																			!x.is_active,
																	},
																);
																await load();
															} catch (e) {
																setError(
																	e?.response
																		?.data
																		?.error ||
																	"No se pudo actualizar.",
																);
															} finally {
																setTogglingEntitlementId(
																	null,
																);
															}
														}}
													>
														<i
															className={
																x.is_active
																	? "ri-close-circle-line d-inline d-md-none"
																	: "ri-checkbox-circle-line d-inline d-md-none"
															}
															aria-hidden="true"
														></i>
														<span className="d-none d-md-inline">
															{entitlementExpired
																? "Vencido"
																: cannotEnableInactivePeriod
																	? "Periodo inactivo"
																: x.is_active
																	? "Deshabilitar curso"
																	: "Habilitar curso"}
														</span>
													</button>
												</td>
											</tr>
										);
									})
								)}
							</tbody>
						</table>
					</div>
				</div>
			</section>

			{/* Periodos de vigencia: se crean automáticamente al asignar un curso (2x1). Solo se listan y se pueden editar/deshabilitar. */}
			<section className="card shadow-sm mb-3 mt-3 card-table">
				<div className="card-body">
					<div>
						<h5 className="mb-2">Periodo de vigencia</h5>
						<div className="text-muted" style={{ fontSize: 12 }}>
							<span>
								Cada periodo se genera al asignar un curso a la
								organización y se sincroniza con las fechas del
								curso. Si necesitas cambiar fechas, edita el
								curso desde el módulo Cursos.
							</span>
						</div>
					</div>

					<div className="table-responsive mt-3 table-wrapper-scroll">
						<table className="table table-hover align-middle mb-0">
							<thead className="table-light">
								<tr>
									<th>
										<span>Inicio</span>
									</th>
									<th>
										<span>Fin</span>
									</th>
									<th>
										<span>
											Cursos asignados a este periodo
										</span>
									</th>
									<th>
										<span>Creado</span>
									</th>
									<th>
										<span>Activo</span>
									</th>
									<th
										colSpan="2"
										className="text-center"
										style={{ width: 220 }}
									>
										<span>Acciones</span>
									</th>
								</tr>
							</thead>
							<tbody>
								{masterContracts.length === 0 ? (
									<tr>
										<td
											colSpan="7"
											className="text-muted py-4"
										>
											<span>
												No hay periodos. Asigna un curso
												abajo; las fechas del curso
												crearán el periodo
												automáticamente.
											</span>
										</td>
									</tr>
								) : (
									masterContracts.map((c) => {
										const contractExpired = isExpiredByDate(
											c.end_date,
											ACCESS_GRACE_DAYS,
										);
										const coursesInPeriod =
											entitlements.filter(
												(e) =>
													Number(e.contract) ===
													Number(c.id),
											);
										return (
											<tr key={c.id}>
												<td>
													<span>
														{formatDate(
															c.start_date,
														)}
													</span>
												</td>
												<td>
													<span>
														{formatDate(c.end_date)}
													</span>
												</td>
												<td>
													{coursesInPeriod.length ===
														0 ? (
														<span className="text-muted">
															<span>
																Ninguno.
															</span>
														</span>
													) : (
														<ul
															className="mb-0 ps-3"
															style={{
																fontSize: 13,
															}}
														>
															{coursesInPeriod.map(
																(ent) => (
																	<div
																		key={
																			ent.id
																		}
																	>
																		<strong>
																			<span>
																				{
																					ent.course_code
																				}
																			</span>
																		</strong>
																		<span>
																			{" "}
																			—{" "}
																			{capitalizeFirstLetter(
																				ent.course_name,
																			)}
																		</span>
																		{!ent.is_active && (
																			<span className="text-muted ms-1">
																				<span>
																					(deshabilitado)
																				</span>
																			</span>
																		)}
																	</div>
																),
															)}
														</ul>
													)}
												</td>
												<td>
													<span>
														{formatDate(
															c.created_at,
														)}
													</span>
												</td>
												<td>
													<span>
														{c.is_active
															? "Sí"
															: "No"}
													</span>
												</td>
												<td className="text-center">
													<button
														type="button"
														className={`btn-sm ${c.is_active ? "adm-del-btn" : "adm-save-btn"} d-inline-flex align-items-center justify-content-center border-0`}
														disabled={
															mcTogglingId ===
															c.id ||
															contractExpired
														}
														title={
															contractExpired
																? "El periodo ya venció y no puede volver a habilitarse."
																: undefined
														}
														onClick={() =>
															toggleMasterContractActive(
																c,
															)
														}
													>
														{mcTogglingId ===
															c.id ? (
															<span
																className="spinner-border spinner-border-sm"
																role="status"
																aria-hidden="true"
															></span>
														) : (
															<>
																<i
																	className={
																		c.is_active
																			? "ri-close-circle-line d-inline d-md-none"
																			: "ri-checkbox-circle-line d-inline d-md-none"
																	}
																	aria-hidden="true"
																></i>
																<span className="d-none d-md-inline ms-md-1">
																	{contractExpired
																		? "Vencido"
																		: c.is_active
																			? "Deshabilitar"
																			: "Habilitar"}
																</span>
															</>
														)}
													</button>
												</td>
												<td className="text-center">
													<button
														type="button"
														className="btn-sm adm-upd-btn d-inline-flex align-items-center justify-content-center border-0"
														onClick={() =>
															openEditMasterContractModal(
																c,
															)
														}
													>
														<i
															className="ri-edit-fill d-inline d-md-none"
															aria-hidden="true"
														></i>
														<span className="d-none d-md-inline ms-md-1">
															Editar
														</span>
													</button>
												</td>
											</tr>
										);
									})
								)}
							</tbody>
						</table>
					</div>
				</div>
			</section>

			<section className="card shadow-sm mt-3 card-table">
				<div className="card-body">
					<h5 className="mb-2">Representantes</h5>

					{lastAccessCode && (
						<div className="alert alert-success text-center py-3" role="alert">
							<i className="ri-mail-send-line d-block mb-2" style={{ fontSize: "2rem", color: "#198754" }}></i>
							<div className="fw-semibold mb-1 fs-6">
								<span>Perfecto, las credenciales fueron enviadas por correo electrónico al representante</span>
							</div>
							<p className="mb-0 text-muted" style={{ fontSize: "0.85rem" }}>
								<span>
									El acceso estará vigente mientras la organización tenga un contrato activo. La primera vez deberá cambiar la contraseña obligatoriamente.
								</span>
							</p>
						</div>
					)}

					{admins.length === 0 ? (
						<div className="text-muted py-4 text-center border rounded bg-light mb-3">
							<i
								className="ri-user-unfollow-line fs-3 d-block mb-2"
								aria-hidden="true"
							></i>
							<span>
								Nadie aún. Usa el botón "Crear representante" de
								arriba.
							</span>
						</div>
					) : (
						<div className="row g-3 mb-3 ">
							{admins.map((u) => (
								<div
									key={u.id}
									className="col-12 col-sm-6 col-lg-4"
								>
									<div
										className={`card h-100 border ${u.is_active ? "repres-active" : "repres-not-active"}`}
									>
										<div className="card-body">
											<div className="d-flex justify-content-between align-items-start mb-2 ">
												<strong className="text-break">
													<span>
														{capitalizeWords(
															u.first_name,
														)}{" "}
														{capitalizeWords(
															u.last_name,
														)}
													</span>
												</strong>
												<span
													className={`${u.is_active ? "repr-btn-abil" : "repr-btn-desabil"}`}
												>
													<span>
														{u.is_active
															? "Activo"
															: "Deshabilitado"}
													</span>
												</span>
											</div>
											<div className="small text-muted mb-1">
												<span
													className="d-block text-truncate"
													title={u.email}
												>
													{u.email}
												</span>
												<span>
													RUT:{" "}
													{formatRut(u.rut) || u.rut}
												</span>
											</div>
											<div className="d-flex gap-1 mt-2 flex-wrap">
												<button
													type="button"
													className={`btn-sm flex-grow-1 ${u.is_active ? "adm-del-btn" : "adm-save-btn"}`}
													disabled={
														disablingAdminId ===
														u.id ||
														(!u.is_active &&
															company &&
															!company.is_active)
													}
													title={
														!u.is_active &&
															company &&
															!company.is_active
															? "No se puede habilitar un representante de una organización desactivada."
															: undefined
													}
													onClick={async () => {
														setDisablingAdminId(
															u.id,
														);
														setError("");
														try {
															if (u.is_active) {
																await disableCompanyAdmin(
																	companyId,
																	u.id,
																);
															} else {
																await enableCompanyAdmin(
																	companyId,
																	u.id,
																);
															}
															await load();
														} catch (e) {
															setError(
																e?.response
																	?.data
																	?.detail ||
																e?.response
																	?.data
																	?.error ||
																"No se pudo actualizar.",
															);
														} finally {
															setDisablingAdminId(
																null,
															);
														}
													}}
												>
													{disablingAdminId ===
														u.id ? (
														<span
															className="spinner-border spinner-border-sm"
															role="status"
															aria-hidden="true"
														></span>
													) : (
														<>
															<i
																className={
																	u.is_active
																		? "ri-close-circle-line me-md-1"
																		: "ri-checkbox-circle-line me-md-1"
																}
																aria-hidden="true"
															></i>
															<span className="d-none d-md-inline">
																{u.is_active
																	? "Deshabilitar"
																	: "Habilitar"}
															</span>
														</>
													)}
												</button>
												<button
													type="button"
													className="btn-sm adm-passw-btn"
													disabled={
														resettingPasswordId ===
														u.id
													}
													title="Reiniciar contraseña y generar una temporal nueva"
													onClick={() =>
														askResetCompanyAdminPassword(
															u,
														)
													}
												>
													{resettingPasswordId ===
														u.id ? (
														<span
															className="spinner-border spinner-border-sm"
															role="status"
															aria-hidden="true"
														></span>
													) : (
														<>
															<i
																className="ri-key-2-line me-md-1"
																aria-hidden="true"
															></i>
															<span className="d-none d-md-inline">
																Reiniciar
																contraseña
															</span>
														</>
													)}
												</button>
											</div>
										</div>
									</div>
								</div>
							))}
						</div>
					)}
				</div>
			</section>

			<ConfirmModal
				open={!!resetPasswordTarget}
				onClose={() => {
					if (resettingPasswordId !== null) return;
					setResetPasswordTarget(null);
				}}
				onConfirm={confirmResetCompanyAdminPassword}
				title="Reiniciar contraseña"
				message={
					resetPasswordTarget
						? `Esto reiniciará la contraseña del usuario ${capitalizeWords(resetPasswordTarget.first_name)} ${capitalizeWords(resetPasswordTarget.last_name)} y generará una nueva contraseña temporal. ¿Deseas continuar?`
						: ""
				}
				confirmLabel={
					resettingPasswordId !== null
						? "Reiniciando..."
						: "Reiniciar contraseña"
				}
				loading={resettingPasswordId !== null}
			/>

			<Modal
				title="Agregar curso a la organización"
				open={entModalOpen}
				onClose={() => !entSaving && setEntModalOpen(false)}
				footer={
					<>
						<button
							className="btn-sm adm-del-btn"
							onClick={() => setEntModalOpen(false)}
							disabled={entSaving}
						>
							<span>Cancelar</span>
						</button>
						<button
							className="btn-sm adm-save-btn"
							onClick={submitEntitlement}
							disabled={entSaving}
						>
							<span>
								{entSaving ? "Guardando..." : "Guardar"}
							</span>
						</button>
					</>
				}
			>
				{alert && (
					<div className={`alert alert-${alert.type}`} role="alert">
						<span>{alert.msg}</span>
					</div>
				)}

				<form onSubmit={submitEntitlement} className="row g-3">
					<div className="col-12">
						<label className="form-label">
							<span>Curso</span>{" "}
							<span className="text-danger">*</span>
						</label>
						<input
							className={`form-control ${entFieldErrors.course_run_id ? "is-invalid" : ""}`}
							value={courseRunQuery} onChange={(e) => {
								setCourseRunQuery(e.target.value);
								if (entFieldErrors.course_run_id)
									setEntFieldErrors((p) => ({
										...p,
										course_run_id: undefined,
									}));
							}}
							placeholder="Escribe para buscar por código o nombre..."
							aria-invalid={!!entFieldErrors.course_run_id}
						/>
						{entFieldErrors.course_run_id && (
							<div className="invalid-feedback">
								{entFieldErrors.course_run_id}
							</div>
						)}

						{courseRunLoading && (
							<div
								className="text-muted mt-1"
								style={{ fontSize: 12 }}
							>
								<div
									className="spinner-border spinner-border-sm me-2"
									role="status"
								></div>
								<span>Buscando…</span>
							</div>
						)}

						{courseRunOptions.length > 0 && (
							<div
								className="list-group mt-2"
							>
								{courseRunOptions.map((r) => (
									<button
										key={r.id}
										type="button"
										className="list-group-item list-group-item-action text-start"
										onClick={() => {
											setEntForm((p) => ({
												...p,
												course_run_id: r.id,
												max_students: r.max_students,
											}));
											setSelectedCourseRun(r);
											setCourseRunQuery(
												`${r.course_code} - ${r.course_name}`,
											);
											setCourseRunOptions([]);
										}}
									>
										<strong>{r.course_code}</strong> —{" "}
										{r.course_name}
									</button>
								))}
							</div>
						)}
						{entForm.course_run_id && (
							<span className="text-muted fs-6 mt-1 mb-0">
								Seleccionado. Las fechas del curso serán el
								periodo de vigencia al guardar.
							</span>
						)}
					</div>

					<div className="col-md-6">
						<label className="form-label">
							<span>
								Cupos máximos en este curso:{" "}
								{selectedCourseRun
									? selectedCourseRun.max_students
									: "—"}
							</span>
						</label>
						<input
							type="number"
							className={`form-control ${entFieldErrors.max_students ? "is-invalid" : ""}`}
							placeholder="Ingresa un numero"
							value={entForm.max_students}
							onChange={(e) => setEntForm((p) => ({
								...p,
								max_students: e.target.value,
							}))
							}
							min="1"
							max={selectedCourseRun?.max_students}
							aria-invalid={!!entFieldErrors.max_students}
						/>
						{entFieldErrors.max_students && (
							<div className="invalid-feedback">
								{entFieldErrors.max_students}
							</div>
						)}
					</div>

					<div className="col-md-6 d-flex align-items-end">
						<div className="form-check">
							<input
								className="form-check-input"
								type="checkbox"
								checked={entForm.is_active}
								onChange={(e) =>
									setEntForm((p) => ({
										...p,
										is_active: e.target.checked,
									}))
								}
								id="entActive"
							/>
							<label
								className="form-check-label"
								htmlFor="entActive"
							>
								<span>Activo</span>
							</label>
						</div>
					</div>

					{/* permite Enter para submit */}
					<button type="submit" className="d-none">
						<span>enviar</span>
					</button>
				</form>
			</Modal>

			<Modal
				title={
					<span>
						{mcEditing
							? "Editar periodo de vigencia"
							: "Nuevo periodo de vigencia"}
					</span>
				}
				open={mcModalOpen}
				onClose={() => {
					if (!mcSaving) {
						setMcModalOpen(false);
						setMcEditing(null);
					}
				}}
				footer={
					<>
						<button
							className="btn-sm adm-del-btn"
							onClick={() => setMcModalOpen(false)}
							disabled={mcSaving}
						>
							<span>Cancelar</span>
						</button>
						<button
							className="btn-sm adm-save-btn"
							onClick={submitMasterContract}
							disabled={mcSaving}
						>
							<span>{mcSaving ? "Guardando..." : "Guardar"}</span>
						</button>
					</>
				}
			>
				{alert && (
					<div className={`alert alert-${alert.type}`} role="alert">
						<span>{alert.msg}</span>
					</div>
				)}

				<form onSubmit={submitMasterContract} className="row g-3">
					<div className="col-12 text-muted small">
						<span>
							Indica desde cuándo hasta cuándo tiene convenio esta
							organización con nosotros.
						</span>
					</div>
					<div className="col-md-6">
						<label className="form-label">
							<span>Fecha de inicio</span>{" "}
							<span className="text-danger">*</span>
						</label>
						<input
							type="date"
							className={`form-control ${mcFieldErrors.start_date ? "is-invalid" : ""}`}
							value={mcForm.start_date}
							placeholder="Selecciona una fecha" onChange={(e) => {
								setMcForm((p) => ({
									...p,
									start_date: e.target.value,
								}));
								if (mcFieldErrors.start_date)
									setMcFieldErrors((p) => ({
										...p,
										start_date: undefined,
									}));
							}}
						/>
						{mcFieldErrors.start_date && (
							<div className="invalid-feedback">
								{mcFieldErrors.start_date}
							</div>
						)}
					</div>
					<div className="col-md-6">
						<label className="form-label">
							<span>Fecha de fin</span>{" "}
							<span className="text-danger">*</span>
						</label>
						<input
							type="date"
							className={`form-control ${mcFieldErrors.end_date ? "is-invalid" : ""}`}
							value={mcForm.end_date}
							placeholder="Selecciona una fecha" onChange={(e) => {
								setMcForm((p) => ({
									...p,
									end_date: e.target.value,
								}));
								if (mcFieldErrors.end_date)
									setMcFieldErrors((p) => ({
										...p,
										end_date: undefined,
									}));
							}}
						/>
						{mcFieldErrors.end_date && (
							<div className="invalid-feedback">
								{mcFieldErrors.end_date}
							</div>
						)}
					</div>
					<div className="col-12">
						<div className="form-check">
							<input
								className="form-check-input"
								type="checkbox"
								checked={mcForm.is_active}
								onChange={(e) =>
									setMcForm((p) => ({
										...p,
										is_active: e.target.checked,
									}))
								}
								id="mcActive"
							/>
							<label
								className="form-check-label"
								htmlFor="mcActive"
							>
								<span>Vigente (se puede usar)</span>
							</label>
						</div>
					</div>
					<button type="submit" className="d-none">
						<span>enviar</span>
					</button>
				</form>
			</Modal>

			<Modal
				title={<span>Crear representante</span>}
				open={claveModalOpen}
				onClose={() => {
					if (!claveSaving) {
						setClaveModalOpen(false);
						setClaveResult(null);
					}
				}}
				footer={
					<>
						<button
							type="button"
							className="btn-sm adm-del-btn"
							onClick={() => setClaveModalOpen(false)}
							disabled={claveSaving}
						>
							<span>{claveResult ? "Cerrar" : "Cancelar"}</span>
						</button>
						{!claveResult && (
							<button
								type="button"
								className="adm-save-btn"
								onClick={submitClaveTemporal}
								disabled={claveSaving}
							>
								<span>
									{claveSaving
										? "Creando..."
										: "Crear representante"}
								</span>
							</button>
						)}
					</>
				}
			>
				{claveResult ? (
					claveResult.error ? (
						<div className="alert alert-danger mb-0" role="alert">
							<i
								className="ri-error-warning-line me-2"
								aria-hidden="true"
							></i>
							<span>{claveResult.error}</span>
						</div>
					) : (
						<div className="alert alert-success mb-0 text-center py-4" role="alert">
							<i className="ri-mail-send-line d-block mb-3" style={{ fontSize: "2.5rem", color: "#198754" }}></i>
							<div className="fw-semibold mb-2 fs-5">
								<span>Perfecto, las credenciales fueron enviadas por correo electrónico</span>
							</div>
							<p className="mb-0 text-muted small">
								<span>
									El acceso estará vigente mientras la organización tenga un contrato activo. La primera vez deberá cambiar la contraseña obligatoriamente.
								</span>
							</p>
						</div>
					)
				) : (
					<form onSubmit={submitClaveTemporal} className="row g-3">
						<div className="col-12">
							<p className="text-muted small mb-0">
								<span>
									Completa los datos de la persona que entrará
									al portal. Recibirá usuario y contraseña; el
									acceso depende del contrato de la
									organización.
								</span>
							</p>
						</div>
						<div className="col-md-6">
							<label className="form-label">
								<span>Nombres</span>{" "}
								<span className="text-danger">*</span>
							</label>
							<input
								className={`form-control ${claveFieldErrors.first_name ? "is-invalid" : ""}`}
								value={claveForm.first_name} onChange={(e) => {
									setClaveForm((p) => ({
										...p,
										first_name: e.target.value,
									}));
									if (claveFieldErrors.first_name)
										setClaveFieldErrors((p) => ({
											...p,
											first_name: undefined,
										}));
								}}
								placeholder="Ej: Juan Carlos"
							/>
							{claveFieldErrors.first_name && (
								<div className="invalid-feedback">
									{claveFieldErrors.first_name}
								</div>
							)}
						</div>
						<div className="col-md-6">
							<label className="form-label">
								<span>Apellidos</span>{" "}
								<span className="text-danger">*</span>
							</label>
							<input
								className={`form-control ${claveFieldErrors.last_name ? "is-invalid" : ""}`}
								value={claveForm.last_name} onChange={(e) => {
									setClaveForm((p) => ({
										...p,
										last_name: e.target.value,
									}));
									if (claveFieldErrors.last_name)
										setClaveFieldErrors((p) => ({
											...p,
											last_name: undefined,
										}));
								}}
								placeholder="Ej: Pérez Soto"
							/>
							{claveFieldErrors.last_name && (
								<div className="invalid-feedback">
									{claveFieldErrors.last_name}
								</div>
							)}
						</div>
						<div className="col-md-6">
							<label className="form-label">
								<span>Correo electrónico</span>{" "}
								<span className="text-danger">*</span>
							</label>
							<input
								type="email"
								className={`form-control ${claveFieldErrors.email ? "is-invalid" : ""}`}
								value={claveForm.email} onChange={(e) => {
									setClaveForm((p) => ({
										...p,
										email: e.target.value,
									}));
									if (claveFieldErrors.email)
										setClaveFieldErrors((p) => ({
											...p,
											email: undefined,
										}));
								}}
								placeholder="Ej: nombre.apellido@correo.com"
							/>
							{claveFieldErrors.email && (
								<div className="invalid-feedback">
									{claveFieldErrors.email}
								</div>
							)}
						</div>
						<div className="col-md-6">
							<label className="form-label">
								<span>RUT</span>{" "}
								<span className="text-danger">*</span>
							</label>
							<input
								className={`form-control ${claveRutError || claveFieldErrors.rut ? "is-invalid" : ""}`}
								value={claveForm.rut} onChange={(e) => {
									setClaveForm((p) => ({
										...p,
										rut: sanitizeRutDigitsOnly(
											e.target.value,
										),
									}));
									if (claveRutError) setClaveRutError("");
									if (claveFieldErrors.rut)
										setClaveFieldErrors((p) => ({
											...p,
											rut: undefined,
										}));
								}}
								onBlur={() => {
									if (!claveForm.rut?.trim()) return;
									const r = commitRutField(claveForm.rut);
									if (!r.ok) {
										setClaveRutError(
											r.message || INVALID_RUT_MESSAGE,
										);
										setClaveFieldErrors((p) => ({
											...p,
											rut:
												r.message ||
												INVALID_RUT_MESSAGE,
										}));
									} else {
										setClaveForm((p) => ({
											...p,
											rut: r.formatted,
										}));
										setClaveRutError("");
										setClaveFieldErrors((p) => ({
											...p,
											rut: undefined,
										}));
									}
								}}
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										e.preventDefault();
										e.currentTarget.blur();
									}
								}}
								placeholder="Ej: 123456789"
								title={RUT_DIGITS_HINT}
								maxLength={10}
							/>
							<p className="form-text small text-muted mb-0">
								{RUT_DIGITS_HINT}
							</p>
							{(claveRutError || claveFieldErrors.rut) && (
								<div className="invalid-feedback">
									{claveRutError || claveFieldErrors.rut}
								</div>
							)}
						</div>
						<button type="submit" className="d-none">
							<span>enviar</span>
						</button>
					</form>
				)}
			</Modal>
		</div>
	);
}
